import { queryCache, useQuery } from "react-query"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useHistory, useLocation } from "react-router-dom"
import { BreadCrumbs } from "./components/SharedComponents/BreadCrumbs"
import { Client } from "./components/MapView/Client"
import { Box, Grid } from "@material-ui/core"
import { Loading } from "components/Loading"
import { fetchLookUp, fetchClientView } from "./api"
import { useRevenueParams } from "./hooks/useRevenueParams"
import BaseSwitch from "./components/SharedComponents/BaseSwitch"
import { Filters } from "./components/Filters"
import { HeaderBox } from "./components/SharedComponents/HeaderBox"
import { DetailsContent } from "./components/DetailsView/DetailsContent"
import { RevenueBox } from "./components/MapView/RevenueBox"
import WorldMap from "./components/WorldMap"
import { BaseMapTable } from "./components/MapView/MapViewTables/BaseMapTable"
import { BaseMapDoubleTable } from "./components/MapView/MapViewTables/BaseMapDoubleTable"
import GCFCheckbox from "./components/DetailsView/GCFCheckbox"
import { useI18n } from "providers/I18n"
import { useGetUserInfo } from "./hooks/useGetUserInfo"

export function RevenueDashboard() {
  const { pathname } = useLocation()
  const history = useHistory()
  const {
    search,
    hasParam,
    brandParam,
    clientParam,
    marketEnabled,
    hasClientViewParamSelected,
    isStream,
    isGCFParam,
    hasParamsLoaded,
  } = useRevenueParams()
  const { t } = useI18n()
  const isViewGCF = isGCFParam === "true"
  const [showDetails, setShowDetails] = useState<boolean>(false)
  const [isGCF, setIsGCF] = useState<boolean>(isViewGCF)
  const cached = queryCache.getQueryData("lookup")
  const { assignedBrand, assignedClients, isGmpUser } = useGetUserInfo()

  const {
    data: lookups,
    isLoading,
    updatedAt,
  } = useQuery("lookup", fetchLookUp, {
    onSuccess: (data) => {
      // prepare the new params and update the url
      const updated = new URLSearchParams(search)

      updated.set("isGCF", isGmpUser ? "true" : isGCFParam)

      !hasParam("market") &&
        updated.set(
          "market",
          data.isMarketFiltered ? data.regions[0].markets[0].name : "All",
        )

      !hasParam("scenario") &&
        updated.set("scenario", data?.globalFiltersScenarioDefault)

      !hasParam("year") && updated.set("year", data?.globalFiltersYearDefault)
      !hasParam("region") && updated.set("region", data?.regionDefault)

      updated.delete("client")

      if (!!assignedBrand) {
        updated.set("brand", assignedBrand)
      } else {
        !hasParam("brand") && updated.set("brand", data?.brandFiltersDefault)
      }

      if (!!assignedClients) {
        updated.set("client", assignedClients[0].clientCode)
      }

      // update the url with the new params
      history.push({ pathname, search: updated.toString() })
    },
    enabled: !search || !cached,
  })

  const key = "clients"
  const url = `client/${clientParam}/`
  const { data: client, isLoading: IsLoadingClient } = useQuery(
    [key, url, search],
    fetchClientView,
    {
      enabled: !!clientParam && clientParam !== "All",
    },
  )

  const handleSwitchChange = useCallback(() => {
    const updated = new URLSearchParams(search)

    if (isGmpUser && !showDetails) {
      setIsGCF(true)
    } else if (!showDetails) {
      if (brandParam === "GCF") {
        setIsGCF(true)
        updated.set("brand", "All")
      } else {
        updated.set("brand", brandParam)
        setIsGCF(false)
      }
    } else {
      if (isGCF && brandParam !== "All") {
        updated.set("brand", brandParam)
        setIsGCF(false)
      } else if (isGCF) {
        updated.set("brand", "GCF")
        setIsGCF(true)
      }
    }
    history.push({ pathname, search: updated.toString() })

    setShowDetails(!showDetails)
  }, [search, isGmpUser, showDetails, history, pathname, brandParam, isGCF])

  const handleCheckboxChange = useCallback(() => {
    const updated = new URLSearchParams(search)
    const prevIsGCF = !isGCF
    updated.set("isGCF", prevIsGCF.toString())
    if (!showDetails) {
      if (!isGCF) {
        updated.set("brand", "GCF")
      } else {
        updated.set("brand", "All")
      }
    }
    history.push({ pathname, search: updated.toString() })
    setIsGCF(!isGCF)
  }, [history, isGCF, pathname, search, showDetails])

  const mapViewDetailsContent = useMemo(() => {
    if (!!clientParam) {
      return (
        <>
          <BaseMapTable type="marketsummary" />
          <BaseMapTable type="clientbrands" />
        </>
      )
    }

    if (brandParam && brandParam !== "All") {
      const hasSpecificBrand = brandParam === "GCF" || isStream
      return (
        <>
          <BaseMapTable type="top20clients" />
          {hasSpecificBrand ? (
            <BaseMapDoubleTable />
          ) : (
            <BaseMapTable type="markets" />
          )}
        </>
      )
    }

    return (
      <>
        <BaseMapTable type="top20clients" />
        {marketEnabled ? (
          <BaseMapTable type="brandbreakdown" />
        ) : (
          <BaseMapDoubleTable />
        )}
      </>
    )
  }, [clientParam, brandParam, marketEnabled, isStream])

  const mapViewPieChartsContent = useMemo(() => {
    return hasClientViewParamSelected ? <Client /> : <RevenueBox />
  }, [hasClientViewParamSelected])

  useEffect(() => {
    const updated = new URLSearchParams(search)
    if (isGmpUser) {
      updated.set("isGCF", "true")
      setIsGCF(true)
      return
    }

    if (brandParam !== "GCF" && !showDetails) {
      updated.set("isGCF", "false")
      setIsGCF(false)
    }

    history.push({ pathname, search: updated.toString() })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandParam])

  if (isLoading || !hasParamsLoaded) {
    return <Loading />
  }

  return (
    <div style={{ position: "relative" }}>
      <div
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          minHeight: "100vh",
          zIndex: 0,
        }}
      >
        <WorldMap
          lookups={lookups}
          updatedAt={updatedAt}
          showDetails={showDetails}
        />
      </div>
      <div
        style={{ padding: 32, position: showDetails ? "inherit" : "static" }}
      >
        <Grid
          container
          justify="space-between"
          style={{
            gap: "16px",
          }}
        >
          <BreadCrumbs
            clientName={client?.name}
            isLoading={IsLoadingClient}
            userRoles={{
              isMarketFinance: !!lookups?.isMarketFiltered,
              isRegionFinance: !!lookups?.isRegionFiltered,
              isGmpUser,
            }}
          />

          <div style={{ zIndex: 1, display: "flex", alignItems: "center" }}>
            <Box
              display="flex"
              justifyContent="flex-end"
              style={{ paddingRight: "var(--spacing-l)" }}
            >
              <BaseSwitch
                label="View"
                checked={!showDetails}
                options={{ left: "Details", right: "Map" }}
                onChange={handleSwitchChange}
              />
            </Box>
            <Box display="flex" justifyContent="flex-end">
              <GCFCheckbox
                label="Enable GCF view"
                checked={isGCF || brandParam === "GCF"}
                onChange={handleCheckboxChange}
                disabled={isGmpUser}
              />
            </Box>
          </div>
        </Grid>

        <Grid container justify="space-between" style={{ gap: "16px" }}>
          <div style={{ zIndex: 1 }}>
            <HeaderBox
              lastUpdated={lookups?.lastUpdated}
              clientName={client?.name}
              isLoading={IsLoadingClient}
              gcfLabel={isViewGCF ? "GCF" : t("revenue.revenuedashboard")}
              gcfView={isGCF}
            />
          </div>
          <Filters
            setIsGcf={setIsGCF}
            isViewGcf={isGCF}
            lookups={lookups}
            showDetails={showDetails}
            client={{
              clientName: client?.name || "",
              clientCode: client?.id || "",
            }}
            isLoadingClient={IsLoadingClient}
            assignedBrand={assignedBrand}
            assignedClients={assignedClients}
          />
        </Grid>

        <Grid
          container
          justify="space-between"
          style={{ paddingTop: 32, gap: "24px" }}
        >
          {!showDetails ? (
            <>
              <Grid item>{mapViewPieChartsContent}</Grid>
              <Grid
                item
                style={{
                  zIndex: 1,
                  display: "flex",
                  gap: "24px",
                  flexDirection: "column",
                }}
              >
                {mapViewDetailsContent}
              </Grid>
            </>
          ) : (
            <DetailsContent isGCF={isGCF} />
          )}
        </Grid>
      </div>
    </div>
  )
}
